import { nextTick } from "vue"
import { createRouter, createWebHistory, START_LOCATION } from "vue-router"

import { useSessionStore } from "@/entities/session"
import { setUnauthorizedHandler } from "@/shared/api/http-client"

import { focusPageHeading } from "./focus-page-heading"

const routes = [
  { path: "/", redirect: { name: "feed" } },
  {
    path: "/login",
    name: "login",
    component: () => import("@/pages/login"),
    meta: { title: "Sign in" },
  },
  {
    path: "/feed",
    name: "feed",
    component: () => import("@/pages/feed"),
    meta: { title: "Feed", requiresAuth: true },
  },
  {
    path: "/sprints",
    name: "sprints",
    component: () => import("@/pages/sprints"),
    meta: { title: "Sprints", requiresAuth: true },
  },
  {
    path: "/sessions",
    name: "sessions",
    component: () => import("@/pages/sessions"),
    meta: { title: "Sessions", requiresAuth: true },
  },
  {
    path: "/journals",
    name: "journals",
    component: () => import("@/pages/journals"),
    meta: { title: "Journals", requiresAuth: true },
  },
  {
    path: "/topics",
    name: "topics",
    component: () => import("@/pages/topics"),
    meta: { title: "Topics", requiresAuth: true },
  },
  {
    path: "/topics/:id",
    name: "topic-detail",
    component: () => import("@/pages/topic-detail"),
    props: true,
    meta: { title: "Topic", requiresAuth: true },
  },
  {
    path: "/factors",
    name: "factors",
    component: () => import("@/pages/factors"),
    meta: { title: "Factors", requiresAuth: true },
  },
  {
    path: "/factors/:id",
    name: "factor-detail",
    component: () => import("@/pages/factor-detail"),
    props: true,
    meta: { title: "Factor", requiresAuth: true },
  },
  {
    path: "/map",
    name: "skill-map",
    component: () => import("@/pages/skill-map"),
    meta: { title: "Skill map", requiresAuth: true },
  },
  {
    path: "/practice-map",
    name: "practice-map",
    component: () => import("@/pages/triggers"),
    meta: { title: "Practice map", requiresAuth: true },
  },
  {
    path: "/timeline",
    name: "timeline",
    component: () => import("@/pages/timeline"),
    meta: { title: "Timeline", requiresAuth: true },
  },
  {
    path: "/daily",
    name: "daily",
    component: () => import("@/pages/daily"),
    meta: { title: "Daily logs", requiresAuth: true },
  },
  {
    path: "/insights",
    name: "insights",
    component: () => import("@/pages/insights"),
    meta: { title: "Insights", requiresAuth: true },
  },
  {
    path: "/asanas",
    name: "asanas",
    component: () => import("@/pages/asanas"),
    meta: { title: "Ashtanga Asanas", requiresAuth: true },
  },
  {
    path: "/asanas/:id",
    name: "asana-detail",
    component: () => import("@/pages/asana-detail"),
    props: true,
    meta: { title: "Asana", requiresAuth: true },
  },
  {
    path: "/me",
    name: "me",
    component: () => import("@/pages/me"),
    meta: { title: "Account", requiresAuth: true },
  },
]

export const router = createRouter({
  history: createWebHistory(),
  routes,
})

router.beforeEach((to) => {
  const session = useSessionStore()
  session.dropIfExpired()
  if (to.meta.requiresAuth && !session.isAuthenticated) {
    return { name: "login", query: { redirect: to.fullPath } }
  }
  if (to.name === "login" && session.isAuthenticated) {
    return { name: "feed" }
  }
  return true
})

let stopFocusing = (): void => {}

router.afterEach((to, from, failure) => {
  if (failure) return
  const page = to.meta.title
  document.title = page ? `${page} · Sikkha` : "Sikkha"
  if (from === START_LOCATION || to.path === from.path) return
  stopFocusing()
  void nextTick(() => {
    stopFocusing = focusPageHeading(page ?? "Sikkha")
  })
})

export async function onUnauthorized(): Promise<void> {
  useSessionStore().logout()
  const current = router.currentRoute.value
  if (current.name !== "login") {
    await router.push({ name: "login", query: { redirect: current.fullPath } })
  }
}

setUnauthorizedHandler(() => void onUnauthorized())
